import { useEffect, useRef, useTransition } from "react"
import { useMatch } from "react-router-dom"

import { useGetReportsJobs } from "@/api/useReports"
import { App } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { isReportJobRunning } from "@/domain/report"
import { predicateHelpers, useQueryClient } from "@/hooks/useHttp"
import { useReportsJobs } from "@/pages/ReportsPage/applications/useReports"

import type { JobReport } from "@/domain/report"
import type { PropsWithChildren } from "react"

export const ReportProvider = ({ children }: PropsWithChildren) => {
  const shouldCheckJobs = useRef(true)
  const match = useMatch(NAMES_ROUTES.REPORTS.ROOT)
  const config = {
    staleTime: Infinity,
    enabled: !!match || !!shouldCheckJobs.current
  }
  const [{ response: responseWithCurrentParams, isFetching }, params] =
    useReportsJobs(config)
  const paramsKeys = Object.keys(params || {})
  const enabled = paramsKeys.length > 1 || `${params["pageNumber"]}` !== "0"
  const { response } = useGetReportsJobs(
    { pageNumber: 0 },
    { ...config, enabled: enabled && config.enabled }
  )
  const data = (enabled ? response?.data : responseWithCurrentParams?.data) as
    JobReport[] | undefined
  const { message } = App.useApp()
  const [_, startTransition] = useTransition()
  const queryClient = useQueryClient()

  const checkJobsState = (data: JobReport[]) => {
    const NAME_SESSION_STORAGE = "running-reports"

    try {
      const running =
        data.filter(isReportJobRunning).map((record) => record.id) || []
      const isRunning = running.length > 0
      const storedValue = (
        sessionStorage.getItem(NAME_SESSION_STORAGE) || ""
      ).split(",")
      const runningSet = new Set(running)

      storedValue.forEach((value) => {
        if (!runningSet.has(value)) {
          const report = data.find((report) => report.id === value)

          if (report)
            message.success(`${report.definitionName} report is ready`)
        }
      })

      sessionStorage.setItem(NAME_SESSION_STORAGE, running.join(","))

      if (!isRunning) {
        shouldCheckJobs.current = false
        return
      }

      setTimeout(() => {
        queryClient.invalidateQueries({
          predicate: predicateHelpers.includesKeysPredicate([
            RESOURCES_NAMES.REPORTS.JOBS
          ])
        })
      }, 5000)
    } catch {
      sessionStorage.removeItem(NAME_SESSION_STORAGE)
    }
  }

  useEffect(() => {
    shouldCheckJobs.current = true

    if (!data || isFetching) return

    startTransition(() => checkJobsState(data))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching])

  return children
}
